import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useClickAway } from 'react-use';
import classNames from 'classnames';
import { fontSize, fontStyle, palette, spacing } from '@playdapp/ui';

import Icon from '@/components/Icon';

const DropDownBlock = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
`;

const FilterArrowIcon = styled(Icon)`
  transition: 0.3s;
`;

const DropDownBox = styled.div<{
  width?: string;
  height?: string;
  isBorder?: boolean;
  isMobile?: boolean;
  isDropBox?: boolean;
  disabled?: boolean;
  isOpen?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: ${({ width }) => (!width ? '340px' : `${width}px`)};
  height: ${({ height }) => height && height}px;
  padding: ${({ isDropBox }) =>
    isDropBox ? `${spacing.s} ${spacing.l}` : spacing.s};
  border: ${({ isBorder }) => isBorder && `1px solid ${palette.gray500}`};
  border-bottom: ${({ isMobile }) =>
    !isMobile && `1px solid ${palette.gray600}`};
  border-radius: ${({ isBorder }) => isBorder && spacing.xs};
  font-size: ${({ isDropBox }) => (isDropBox ? fontSize.p3 : fontSize.p4)};
  font-weight: 400;
  background-color: ${({ isOpen }) =>
    isOpen ? `${palette.gray200}` : `${palette.white}`};
  color: ${palette.dgray400};
  cursor: pointer;

  ${({ isMobile }) =>
    isMobile &&
    css`
      width: 100%;
      font-size: ${fontSize.p5};
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      background: ${palette.gray200};
      color: ${palette.gray600};
      cursor: not-allowed;
    `}

    &:hover {
    background-color: ${palette.gray100};
  }

  ${FilterArrowIcon} {
    transform: ${({ isOpen }) => isOpen && 'rotateX(180deg)'};
  }
`;

const DropDownContent = styled.div<{
  floatingContent?: boolean;
  top?: string;
  isMobile?: boolean;
}>`
  display: flex;
  position: ${({ floatingContent }) => floatingContent && 'absolute'};
  top: ${({ floatingContent, top }) => (floatingContent && top ? top : '0')}px;
  flex-direction: column;
  width: 100%;
  min-width: ${({ isMobile, theme }) => isMobile && theme.rem(200)};
  z-index: ${({ floatingContent }) => floatingContent && '10'};
  color: ${palette.dgray200};
  ${fontStyle('p3')}
  border-radius: 8px;
  border: 1px solid #f5f5f7;
  box-shadow: 0px 20px 32px rgba(76, 79, 110, 0.1);
  background: ${palette.white};

  &:last-of-type {
    border-bottom: 0;
  }
`;

type Props = {
  className?: string;
  title: string;
  collectionName?: string;
  width?: string;
  height?: string;
  isBorder?: boolean;
  floatingContent?: boolean;
  top?: string;
  children?: JSX.Element;
  isMobile?: boolean;
  isDropBox?: boolean;
  isClose?: boolean;
  disabled?: boolean;
};

const SortByDropDown = ({
  className,
  title,
  collectionName,
  width,
  height,
  isBorder,
  floatingContent,
  top,
  children,
  isMobile,
  isDropBox,
  isClose,
  disabled,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerElement = useRef(null);

  useClickAway(containerElement, () => {
    setIsOpen(false);
  });

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    setIsOpen(collectionName === 'all');
  }, [collectionName]);

  useEffect(() => {
    if (isClose) {
      setIsOpen(false);
    }
  }, [isClose]);

  return (
    <DropDownBlock className={className} ref={containerElement}>
      <DropDownBox
        width={width}
        height={height}
        isBorder={isBorder}
        onClick={handleToggle}
        isMobile={isMobile}
        isDropBox={isDropBox}
        disabled={disabled}
        isOpen={isOpen}
      >
        {title}
        <FilterArrowIcon
          name="chevron-down-0206"
          size={10}
          className={classNames({ open: isOpen })}
        />
      </DropDownBox>
      {isOpen && !disabled && (
        <DropDownContent
          floatingContent={!!floatingContent}
          top={top}
          isMobile={isMobile}
          onClick={() => setIsOpen(!isOpen)}
        >
          {children}
        </DropDownContent>
      )}
    </DropDownBlock>
  );
};
export default SortByDropDown;
